<template lang="pug">
#questionnaires-edit(v-cloak)
  .box(@click="resetFocus")
    .box-header.d-flex.flex-row
      .titles
        .title
          span アンケートの修正
        .note
          span アンケート項目は「選択肢」「テキスト」を選択できます（最大50項目）。
          br
          span 選択肢の場合、選択肢を50つまで作成できます。

    .box-body.register-body(v-if="questionnaire")
      .sub-title.mb-2 ①アンケート項目の作成
      .questionnaire-contents.mb-3(v-if="!questionnaire.sendDatetime")
        FormInput(v-model="title" key-name="タイトル" type="text" name="title" placeholder="タイトル"
          :maxlength="100" :validation-rules="{ required: true, max: 100 }" required)
        FormTextarea(v-model="text" key-name="説明文" name="text" 
          placeholder="アンケート回答前に確認いただきたい内容（目的など）を記載してください。\n回答期限や回答所要時間も記載すると良いと思います。"
          :maxlength="1000" :validation-rules="{ max: 1000 }"
          :styles="{ maxWidth: '600px', width: '600px', minHeight: '160px' }")
        FormMultiFile(:registerImageFilesLimit="_registerImageFilesLimit" :enablePreviewStatus="_enablePreviewStatus"
          @inputFiles="inputFiles" @onTempFilePreview="onTempFilePreview")
        .register-row
          DraggableFiles(v-model="files" @delete="onFileDelete" :item-width="'468px'")
        FormCheck(v-model="hidden" key-name="匿名回答" name="hidden" label="回答者を匿名にする")
          template(#description)
            span.error-message ※匿名にすると、回答結果に回答者の情報は表示されません。

        .questionnaires
          .mb-2
            span.sub-title.mr-1 質問項目
            span.validation-label 必須
          .questionnaire.mb-2(v-for="(question, index) in questions" @click.stop="focusInQuestion(index)")
            QuestionInputItem(v-model="questions[index]" :index="index" :disableMoveDown="index == (questions.length -1)" :disableMoveUp="index == 0"
              @onDelete="onQuestionDelete" @onOptionDelete="onQuestionOptionDelete" @onAddSelectOption="onQuestionAddSelectOption"
              @onMoveDown="onQuestionMoveDown" @onMoveUp="onQuestionMoveUp")
        .add-question-buttons
          .d-flex.flex-row.align-items-center
            select.input-select.mr-2(v-model="questionType" style="width: 240px;")
              option(v-for="option in questionTypeOptions" :value="option.value") {{ option.label }}
            button.btn.add-question-btn.mr-2(@click="addQuestion(questionType)"
              :disabled="_disableQuestionnaireBtn" :class="{ 'disable': _disableQuestionnaireBtn }") 質問項目追加
            button.btn.preview-btn(@click="previewQuestion" :disabled="_disablePreviewBtn" :class="{ 'disable': _disablePreviewBtn }") プレビュー
        .register-row.mt-2(v-show="questionErrorMessage")
          .register-group
            .register-value
              .error-message {{ questionErrorMessage }}

      .questionnaire-convertNumberToString(v-else)
        span.accepted-message 送信後は修正できません
      .mb-4

      .sub-title.mb-2 ②回答期限の設定
      .questionnaire-contents
        FormCheck(v-model="enableAnswerLimit" key-name="" name="enable-answer-limit" label="回答期限を設定する")
        FormDatetimePicker(v-if="enableAnswerLimit == true" v-model="replyLimit" :date.sync="replyLimitDate" :time.sync="replyLimitTime"
          placeholder="回答期限" key-name="日付・時間" name="answer-limit")
        AcceptStatusSwitch.accept-switch.mt-2(v-if="questionnaire.sendDatetime" v-model="acceptingAnswers" style="margin-left:20px;")
      .mb-4

      .sub-title.mb-2 ③メッセージ送信内容の作成
      .questionnaire-contents(v-if="!questionnaire.sendDatetime")
        SendMessageTargetCheck(v-model="targetType")
        SendMessageTargetsInput(ref="sendMessageTargetsInput" v-model="targetType" :groupType="groupType"
          @onFamiliesSelect="onFamiliesSelect" @onOtherUserGroupsSelect="onOtherUserGroupsSelect" @onSendMessageGroupSelect="onSendMessageGroupSelect"
          @loadedFamiliesTargets="loadedFamiliesTargets" @loadedOtherUserTargets="loadedOtherUserTargets" @loadedGroupTargets="loadedGroupTargets")
        .register-row.mt-2(v-show="targetErrorMessage")
          .register-group
            .register-value
              .error-message {{ targetErrorMessage }}
        FormInput(v-model="messageTitle" key-name="タイトル" name="message-title" type="text" placeholder="送信メッセージタイトル" :maxlength="100"
          :validation-rules="{ required: true, max: 100 }" required)
        FormTextarea(v-model="messageText" key-name="本文" name="message-text" placeholder="送信メッセージ本文を入力してください。" :maxlength="1000"
          :validation-rules="{ required: true, max: 1000 }" :styles="{ maxWidth: '600px', width: '600px', minHeight: '160px' }" required)
      .questionnaire-contents(v-else)
        span.accepted-message 送信後は修正できません
      .mb-4

      .sub-title ④共有ユーザーの設定
      .share-standard-user.mb-2(v-if="_showShareStandardUser")
        .share-standard-users
          div 共有されたユーザーは修正・回答結果の閲覧ができます。
          div 送信後も共有ユーザーの設定変更ができます。
          div.warning-message ※ 管理者ユーザーには必ず共有されます。
          div.warning-message ※ 作成者を共有ユーザーから外すことはできません。
          div.warning-message ※ 一般ユーザーはアンケートの使用機能権限も合わせて必要です。
        ShareStandardUserCheckboxs(v-model="shareStandardUsers")
      .share-standard-users(v-else)
        span 管理者ユーザーまたは作成者以外は修正できません。

      .register-row.mt-3(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer(v-if="questionnaire")
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(v-if="!questionnaire.sendDatetime" @click="showUpdateConfirmModal(questionnaire)" type="button") 一時保存
        button.btn.send-btn(v-if="!questionnaire.sendDatetime" @click="showSendConfirmModal" type="button") 送信
        button.btn.send-btn(v-if="questionnaire.sendDatetime" @click="showUpdateConfirmModal(questionnaire)" type="button") 更新
        button.btn.cancel-btn(@click="gotoQuestionnaireList" type="button") キャンセル

  SendMessageSelectModal(ref="sendMessageSelectModal" @add="addFamiliesTarget")
  SendMessageOtherUserGroupSelectModal(ref="otherUserGroupSelectModal" @add="addOtherUserGroupTarget")
  SendMessageGroupSelectModal(ref="sendMessageGroupSelectModal" :planType="planType" :groupType.sync="groupType" @add="addSendGroupTarget")

  QuestionnaireFilePreviewModal(ref="filePreviewModal")
  PreviewQuestionnairesMoldal(v-model="showPreviewModal" :title="title" :text="text" :questions="questions")

  OkCancelModal(v-model="showUpdateModal" :title="updateModalTitle" @ok="updateQuestionnaire")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoQuestionnaireList")

  OkCancelModal(v-model="showSendModal" title="アンケートを送信します。よろしいですか？" @ok="sendQuestionnaire")
  OkModal(v-model="showSendSuccessModal" title="アンケート送信を受け付けました。" @ok="gotoQuestionnaireList")

  //- file upload error modal
  FileUploadErrorModal(v-model="showUploadErrorModal")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
  ProgressLoading(v-model="currentUploadCount" :max="uploadCount" :isBusy="progressLoading" :message="progressMessage")
</template>

<script lang="ts">
import { Component, Inject, Ref, Vue } from "vue-property-decorator";
import { userStore, screenInputTemporaryStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import Setting from "../../../../public/setting.json";

import SystemError from "@/commons/models/SystemError";
import { SendMessageTargetGroup } from "@/commons/models/SendMessageTarget";
import { OtherUserGroupTypeGroup } from "@/commons/services/OtherUserGroupTypeMembers";
import { Role } from "@/commons/services/Authorization";

import QuestionnairesService, {
  TemplateType,
  QuestionnairesGetModel,
  TargetType,
  QuestionTemplateItem,
  QuestionnairesPatchModel,
  FileUrl
} from "@/commons/services/Questionnaires";
import { PlanType } from "@/commons/services/Plans";
import StandardAccountsService, { StandardAccountsGetModel } from "@/commons/services/StandardAccounts";
import S3LocationsService from "@/commons/services/S3Locations";
import { S3LocationRequest, S3LocationResponse, S3LocationType, S3LocationMessage } from "@/commons/models/S3Location";
import S3UploadsService from "@/commons/services/S3Uploads";
import ExistS3FilesService, { ExistS3FilesPostModel } from "@/commons/services/ExistS3Files";
import S3UrlService from "@/commons/services/S3Url";

import QuestionnaireFileCommon from "@/commons/services/QuestionnaireFileCommon";

import FileUploadErrorModal from "@/pc/basics/FileUploadErrorModal.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ProgressLoading from "@/pc/basics/ProgressLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import FormMultiFile from "@/pc/containers/questionnaires/FormMultiFile.vue";
import DraggableFiles, { ExtDraggableFile } from "@/pc/components/ExtDraggableFiles.vue";
import FormCheck from "@/pc/components/forms/FormCheck.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";
import QuestionInputItem, { QuestionInputItemModel, QuestionInputSelectItemModel, QuestionInputTextItemModel } from "@/pc/containers/questionnaires/QuestionInputItem.vue";
import AcceptStatusSwitch from "@/pc/containers/questionnaires/AcceptStatusSwitch.vue";
import SendMessageTargetCheck, { SendMessageTarget } from "@/pc/containers/questionnaires/SendMessageTargetCheck.vue";
import SendMessageTargetsInput, { GroupModel } from "@/pc/containers/questionnaires/SendMessageTargetsInput.vue";
import SendMessageSelectModal from "@/pc/components/modals/SendMessageSelectModal.vue";
import SendMessageOtherUserGroupSelectModal from "@/pc/components/modals/SendMessageOtherUserGroupSelectModal.vue";
import SendMessageGroupSelectModal from "@/pc/containers/questionnaires/SendMessageGroupSelectModal.vue";
import ShareStandardUserCheckboxs, { ShareStandardUserCheckbox } from "@/pc/containers/questionnaires/ShareStandardUserCheckBoxs.vue";
import PreviewQuestionnairesMoldal from "@/pc/containers/questionnaires/PreviewQuestionnaireModal.vue";
import QuestionnaireFilePreviewModal from "@/pc/containers/questionnaires/QuestionnaireFilePreviewModal.vue";
import { GroupType } from "@/pc/components/modals/SendMessageGroupSelectModal.vue";
/**
 * ### アンケート登録画面コンポーネント
 */
@Component({
  components: {
    FileUploadErrorModal,
    PageLoading,
    ProgressLoading,
    ValidationAlert,
    OkCancelModal,
    OkModal,
    HelpIcon,
    FormInput,
    FormTextarea,
    FormMultiFile,
    DraggableFiles,
    FormCheck,
    FormDatetimePicker,
    QuestionInputItem,
    ShareStandardUserCheckboxs,
    AcceptStatusSwitch,
    SendMessageTargetCheck,
    SendMessageTargetsInput,
    SendMessageSelectModal,
    SendMessageOtherUserGroupSelectModal,
    QuestionnaireFilePreviewModal,
    PreviewQuestionnairesMoldal,
    SendMessageGroupSelectModal
  },
  $_veeValidate: { validator: "new" }
})
export default class QuestionnaireEdit extends Vue {
  /** アンケートサービス */
  @Inject("QuestionnairesService") questionnairesService!: QuestionnairesService;
  /** アカウントサービス */
  @Inject("StandardAccountsService") standardAccountsService!: StandardAccountsService;
  /** S3格納先URLサービス */
  @Inject("S3LocationsService") s3LocationsService!: S3LocationsService;
  /** S3ファイルアップロードサービス */
  @Inject("S3UploadsService") s3UploadsService!: S3UploadsService;
  /** S3ファイル存在確認サービス */
  @Inject("ExistS3FilesService") existS3FilesService!: ExistS3FilesService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  @Ref()
  sendMessageTargetsInput!: SendMessageTargetsInput;
  @Ref()
  sendMessageSelectModal!: SendMessageSelectModal;
  @Ref()
  otherUserGroupSelectModal!: SendMessageOtherUserGroupSelectModal;
  @Ref()
  sendMessageGroupSelectModal!: SendMessageGroupSelectModal;
  @Ref()
  filePreviewModal!: QuestionnaireFilePreviewModal;

  /** 添付許可Content-Typeリスト */
  readonly allowContentType = ["image/jpeg", "image/png", "application/pdf", "video/mp4", "video/quicktime"];
  readonly allowMessageType = ["image", "image", "pdf", "video", "video"];

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 登録確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新確認モーダルタイトル */
  updateModalTitle: string = "";
  /** 登録完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;
  /** 送信確認モーダル表示可否 */
  showSendModal: boolean = false;
  /** 送信完了モーダル表示可否 */
  showSendSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 現在のファイルアップロード数 */
  currentUploadCount: number = 0;
  /** ファイルアップロード総数 */
  uploadCount: number = 0;
  /** プログレスモーダルメッセージ */
  progressMessage: string = "";
  /** プログレスモーダル表示可否 */
  progressLoading: boolean = false;

  /** プレビューモーダル表示可否 */
  showPreviewModal: boolean = false;

  /** プラン種別 */
  planType: PlanType = PlanType.UNKNOWN;

  /** アンケートタイトル */
  title: string = "";
  /** アンケート説明文 */
  text: string = "";
  /** 実際に登録するファイル */
  files: Array<ExtDraggableFile> = [];
  /** 匿名にするか否か */
  hidden: boolean = false;
  /** 追加する質問項目の種類 */
  questionType: TemplateType = TemplateType.SELECT;
  /** 追加する質問項目の種類選択肢 */
  questionTypeOptions: Array<{ value: number | string; label: string }> = [
    { value: TemplateType.SELECT, label: "選択肢" },
    { value: TemplateType.TEXT, label: "テキスト" }
  ];
  /** アンケート内容 */
  questions: Array<QuestionInputItemModel> = [];

  /** 回答期限を設定するか */
  enableAnswerLimit: boolean = false;
  /** 期日 */
  replyLimit: number | null = null;
  /** 期日(日付) */
  replyLimitDate: string = "";
  /** 期日(時間) */
  replyLimitTime: string = "";
  /** 受付状態 */
  acceptingAnswers: boolean | null = null;

  /** 宛先種別 */
  targetType: SendMessageTarget | null = null;
  /** 宛先グループの種別（宛先グループや利用者グループなど） */
  groupType: GroupType = GroupType.UNKNOWN;
  /** 宛先種別 */
  targets: string[] = [];
  /** メッセージタイトル */
  messageTitle: string = "";
  /** メッセージ本文 */
  messageText: string = "";

  /** 更新元アンケート */
  questionnaire: QuestionnairesGetModel | null = null;
  /** 共有一般ユーザー */
  shareStandardUsers: Array<ShareStandardUserCheckbox> = [];

  /** エラーメッセージ */
  errorMessage: string = "";
  /** 質問項目のバリデーションエラーメッセージ */
  questionErrorMessage: string = "";
  /** 宛先のバリデーションエラーメッセージ */
  targetErrorMessage: string = "";
  /** ファイルアップロードエラーモーダル表示可否 */
  showUploadErrorModal: boolean = false;

  /** ファイル登録数 */
  readonly registerFileUploadLimit: number = 3;

  /**
   * ### 質問項目追加ボタンの非活性を返す
   */
  get _disableQuestionnaireBtn(): boolean {
    if (this.questions.length < 50) {
      return false;
    } else {
      return true;
    }
  }

  /**
   * ### プレビューボタンの非活性を返す
   */
  get _disablePreviewBtn(): boolean {
    if (0 < this.questions.length) {
      return false;
    } else {
      return true;
    }
  }

  /**
   * ### 登録可能なファイル数を返す
   */
  get _registerImageFilesLimit(): number {
    const limit: number = this.registerFileUploadLimit - this.files.length;
    return limit;
  }

  /**
   * ### プレビュー表示可否を返す
   */
  get _enablePreviewStatus(): boolean {
    return 0 < this.files.length ? true : false;
  }

  get _showShareStandardUser(): boolean {
    // アンケート情報未取得の場合は非表示
    if (!this.questionnaire) {
      return false;
    }
    // 一般ユーザー以外であれば表示
    if (userStore.loginUser.role != Role.STANDARD_USER) {
      return true;
    }
    // 記載者、送信者(初回)に自分のIDがあれば表示
    if (userStore.loginUser.userId == this.questionnaire.writer || userStore.loginUser.userId == this.questionnaire.sender) {
      return true;
    }
    // 再送者に自分のIDがあれば表示
    if (0 < this.questionnaire.histories.length) {
      for (const history of this.questionnaire.histories) {
        if (history.sender == userStore.loginUser.userId) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   *
   * - 期日の初期値を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    this.planType = userStore.facility.planType;

    const id: number = Number(this.$route.params.id);
    const questionnaire: QuestionnairesGetModel = await this.getQuestionnaire(id);
    this.questionnaire = questionnaire;
    await this.setQuestionnaire(questionnaire);

    const shareStandardUsers: Array<ShareStandardUserCheckbox> = await this.createShareStandardUsers(questionnaire);
    this.shareStandardUsers = shareStandardUsers;

    // TODO 宛先(宛先グループ)が読込み終わらないと読み込み中を解除できない
    // loadedGroupTargets()が呼び出されたら完了だが、ここで制御できるようにしたい
    // 送信済みの場合はloadedGroupTargets()が呼ばれないのでここで読込み中を解除する必要がある
    if (questionnaire.sendDatetime) {
      this.isBusy = false;
    }

    // storeに一時保存した入力内容を復元する
    this.restoreScreenInputTemporaryStore();

    return;
  }

  /**
   * ### アンケートを取得する
   */
  public async getQuestionnaire(id: number): Promise<QuestionnairesGetModel> {
    const response: QuestionnairesGetModel = await this.questionnairesService.get(userStore, id);
    return response;
  }

  /**
   * ### 画面表示用のアンケート情報を格納する
   */
  public async setQuestionnaire(questionnaire: QuestionnairesGetModel): Promise<void> {
    this.title = questionnaire.title ? questionnaire.title : "";
    this.text = questionnaire.text ? questionnaire.text : "";

    this.files = [];
    if (questionnaire.fileUrls && 0 < questionnaire.fileUrls.length) {
      for (const fileUrl of questionnaire.fileUrls) {
        this.files.push({
          name: fileUrl.name,
          type: "existing",
          fileObject: undefined,
          url: fileUrl.url
        });
      }
    }

    this.hidden = questionnaire.hidden;
    this.questions = this.createQuestions(questionnaire.questions);

    if (questionnaire.replyLimit) {
      this.enableAnswerLimit = true;
      this.replyLimit = questionnaire.replyLimit;
      this.replyLimitDate = this.$dateUtil.convertNumberToString(questionnaire.replyLimit, "yyyy-mm-dd");
      this.replyLimitTime = this.$dateUtil.convertNumberToString(questionnaire.replyLimit, "hh:MM:ss");
    } else {
      this.enableAnswerLimit = false;
      this.replyLimit = null;
      const replyLimitDate: Date = new Date();
      replyLimitDate.setDate(replyLimitDate.getDate() + 7);
      this.replyLimitDate = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
      this.replyLimitTime = "10:00:00";
    }
    this.acceptingAnswers = questionnaire.acceptingAnswers;

    this.targetType = this.createSendTargetType(questionnaire.targetType);
    this.groupType = this.createGroupType(questionnaire);
    this.messageTitle = questionnaire.messageTitle ? questionnaire.messageTitle : "";
    this.messageText = questionnaire.messageText ? questionnaire.messageText : "";

    return;
  }

  /**
   * ### S3に格納されているFileを取得する
   *
   * @param url 元URL
   */
  public async getS3File(url: string, fileName: string): Promise<File> {
    const response: File = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, url, fileName);
    return response;
  }

  /**
   * ### 子コンポーネントでファイルが選択された場合のイベント処理
   *
   * - ファイルが上限数以上なら無視する
   * - バリデーションを行う
   * - バリデーションエラーでなければ、ファイルを追加する
   */
  public async inputFiles(files: Array<File>): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const questionnaireFileCommon: QuestionnaireFileCommon = new QuestionnaireFileCommon(this.$validator, "tempfiles-multiple-register-errors");
    const addFiles: Array<ExtDraggableFile> = await questionnaireFileCommon.createAddFile(files, this.registerFileUploadLimit, this.files.length);

    if (0 < addFiles.length) {
      this.files = this.files.concat(addFiles);
      for (let i = 0; i < this.files.length; i++) {
        this.files[i].key = i.toString();
      }
    }

    this.isBusy = false;
    return;
  }

  /**
   * ### 添付ファイルプレビューボタン押下イベント処理
   *
   * - ダイアログで添付ファイルをプレビュー表示する
   */
  public async onTempFilePreview(): Promise<void> {
    // ファイルを取得していないものがあったら取得する
    const files = [];
    for (const file of this.files) {
      this.isBusy = true;
      this.loadingMessage = "読込み中...";

      if (!file.fileObject) {
        file.fileObject = await this.getS3File(file.url as string, file.name);
        files.push(file.fileObject);
      } else {
        files.push(file.fileObject);
      }

      this.isBusy = false;
    }

    // ファイルをモーダルで表示する
    if (this.filePreviewModal && this.filePreviewModal instanceof QuestionnaireFilePreviewModal) {
      this.filePreviewModal.show("", false, files);
    }
    return;
  }

  /**
   * ### 指定されたファイルを削除する
   */
  public onFileDelete(index: number): void {
    this.files.splice(index, 1);
    return;
  }

  /**
   * ### 画面表示用の質問項目を作成する
   */
  public createQuestions(questionItems: Array<QuestionTemplateItem>): Array<QuestionInputItemModel> {
    const questions: Array<QuestionInputItemModel> = [];
    for (const questionItem of questionItems) {
      questions.push({
        ...questionItem,
        isFocused: false
      });
    }
    return questions;
  }

  /**
   * ### 画面用の宛先種別を作成する
   */
  public createSendTargetType(targetType: TargetType): SendMessageTarget | null {
    switch (targetType) {
      case TargetType.ALL_FAMILIES: {
        return SendMessageTarget.ALL_FAMILIES;
      }
      case TargetType.FAMILIES: {
        return SendMessageTarget.FAMILIES;
      }
      case TargetType.OTHER_USERS: {
        return SendMessageTarget.OTHER_USERS;
      }
      case TargetType.SEND_MESSAGE_GROUPS:
      case TargetType.RESIDENT_GROUPS:
      case TargetType.OTHER_USER_GROUPS: {
        return SendMessageTarget.GROUPS;
      }
    }
  }

  /**
   * ### 画面用のグループ種別を作成する
   */
  public createGroupType(questionnaire: QuestionnairesGetModel): GroupType {
    switch (questionnaire.targetType) {
      case TargetType.ALL_FAMILIES:
      case TargetType.FAMILIES:
      case TargetType.OTHER_USERS: {
        return GroupType.UNKNOWN;
      }
      case TargetType.SEND_MESSAGE_GROUPS: {
        if (questionnaire.sendMessageGroupInfos && 0 < questionnaire.sendMessageGroupInfos.length && questionnaire.sendMessageGroupInfos[0].type == 0) {
          return GroupType.FAMILY;
        } else {
          return GroupType.OTHER_USER;
        }
      }
      case TargetType.RESIDENT_GROUPS: {
        return GroupType.RESIDENT_GROUP;
      }
      case TargetType.OTHER_USER_GROUPS: {
        return GroupType.OTHER_USER_GROUP;
      }
    }
  }

  /**
   * ### 共有ユーザーの選択肢を作成する
   */
  public async createShareStandardUsers(questionnaire: QuestionnairesGetModel): Promise<Array<ShareStandardUserCheckbox>> {
    const accounts: Array<StandardAccountsGetModel> = await this.getAccounts();
    const sharedStandardUsersMap: Map<string, string> = new Map();
    for (const userId of questionnaire.shareStandardUsers) {
      sharedStandardUsersMap.set(userId, userId);
    }
    const checkboxs: Array<ShareStandardUserCheckbox> = [];
    for (const account of accounts) {
      const userId: string | undefined = sharedStandardUsersMap.get(account.userId);
      const answer: boolean = userId ? true : false;
      checkboxs.push({
        id: account.userId,
        answer: answer,
        name: account.name,
        disabled: userStore.loginUser.userId == account.userId ? true : questionnaire.writer == account.userId ? true : false
      });
    }
    return checkboxs;
  }

  /**
   * ### 一般ユーザーを取得する
   */
  public async getAccounts(): Promise<Array<StandardAccountsGetModel>> {
    const response: Array<StandardAccountsGetModel> = await this.standardAccountsService.get(userStore);
    return response;
  }

  /**
   * ### 質問項目を追加する
   */
  public addQuestion(type: TemplateType): void {
    switch (type) {
      case TemplateType.TEXT: {
        const textItem: QuestionInputTextItemModel = {
          type: type,
          title: "",
          answer: "",
          isLong: false,
          required: false,
          isFocused: false
        };
        this.questions.push(textItem);
        break;
      }
      case TemplateType.SELECT: {
        const selectItem: QuestionInputSelectItemModel = {
          type: type,
          title: "",
          answer: [],
          options: [
            { text: "", value: "1" },
            { text: "", value: "2" }
          ],
          multiple: false,
          required: false,
          isFocused: false
        };
        this.questions.push(selectItem);
        break;
      }
    }
    this.$bvToast.hide("add-questionnaire-toast");
    return;
  }

  /** ### プレビューを表示する */
  public previewQuestion(): void {
    this.showPreviewModal = true;
    return;
  }

  /**
   * ### 質問項目を削除する
   */
  public onQuestionDelete(index: number): void {
    this.questions.splice(index, 1);
    return;
  }

  /**
   * ### 質問項目を一つ後ろに移動する
   */
  public onQuestionMoveDown(index: number): void {
    if (index < 0 || this.questions.length - 1 <= index) {
      // インデックスが範囲外の場合は何もしない
      return;
    }
    // 要素を一つ後ろに移動する
    const target = this.questions[index];
    this.questions.splice(index, 1);
    this.questions.splice(index + 1, 0, target);
    return;
  }

  /**
   * ### 質問項目を一つ前に移動する
   */
  public onQuestionMoveUp(index: number): void {
    // インデックスが範囲外の場合は何もしない
    if (index <= 0 || this.questions.length <= index) {
      return;
    }
    // 要素を一つ前に移動する
    const target = this.questions[index];
    this.questions.splice(index, 1);
    this.questions.splice(index - 1, 0, target);
    return;
  }

  /**
   * ### 質問項目(select)の要素を削除する
   */
  public onQuestionOptionDelete(indexs: { index: number; optionIndex: number }): void {
    const selectItem: QuestionInputSelectItemModel = this.questions[indexs.index] as QuestionInputSelectItemModel;
    selectItem.options.splice(indexs.optionIndex, 1);
    return;
  }

  /**
   * ### 質問項目(select)の要素を追加する
   */
  public onQuestionAddSelectOption(index: number): void {
    const selectItem: QuestionInputSelectItemModel = this.questions[index] as QuestionInputSelectItemModel;
    selectItem.options.push({
      text: ``,
      value: `${selectItem.options.length + 1}`
    });
    return;
  }

  /**
   * ### 質問項目のフォーカスリセット
   *
   * - 質問項目以外がクリックされた際に呼ばれる
   * - 質問項目以外にフォーカスがあっているように見せる
   */
  public resetFocus(): void {
    for (let i = 0; i < this.questions.length; i++) {
      this.questions[i].isFocused = false;
    }
    return;
  }

  /**
   * ### 質問項目のフォーカスセット
   *
   * - 選択されていない項目のフォーカスはfalseにすることで疑似的にフォーカスを当てているように見せる
   */
  public focusInQuestion(index: number): void {
    for (let i = 0; i < this.questions.length; i++) {
      if (i == index) {
        this.questions[i].isFocused = true;
      } else {
        this.questions[i].isFocused = false;
      }
    }
    return;
  }

  /**
   * ### 家族個別の読み込み完了イベント
   *
   * - 子コンポーネントから呼び出される
   * - 一時保存されたアンケート情報の送信種別が家族個別であれば子コンポーネントに宛先を渡して復元させる
   */
  public loadedFamiliesTargets(): void {
    if (this.questionnaire && this.questionnaire.targetType == TargetType.FAMILIES) {
      this.sendMessageTargetsInput.restoreFamilyTargets(this.questionnaire.targets);
    }
    return;
  }

  /**
   * ### 家族の宛先ボタン押下イベント
   *
   * - モーダルで家族を選択させる
   * - キャンセルボタン押下も考慮し、宛先家族の情報はコピーしたものを渡す
   */
  public onFamiliesSelect(familiesTargets: Array<SendMessageTargetGroup>): void {
    const copy: Array<SendMessageTargetGroup> = JSON.parse(JSON.stringify(familiesTargets));
    this.sendMessageSelectModal.show(copy);
    return;
  }

  /**
   * ### 家族個別の宛先選択モーダルも完了イベント
   */
  public addFamiliesTarget(targets: Array<SendMessageTargetGroup>): void {
    this.sendMessageTargetsInput._familiesTargets = targets;
    return;
  }

  /**
   * ### 施設関係者個別の読み込み完了イベント
   *
   * - 子コンポーネントから呼び出される
   * - 一時保存されたアンケート情報の送信種別が施設関係者個別であれば子コンポーネントに宛先を渡して復元させる
   */
  public loadedOtherUserTargets(): void {
    if (this.questionnaire && this.questionnaire.targetType == TargetType.OTHER_USERS) {
      this.sendMessageTargetsInput.restoreOtherUserTargets(this.questionnaire.targets);
    }
    return;
  }

  /**
   * ### 施設関係者の宛先ボタン押下イベント
   *
   * - モーダルで施設関係者を選択させる
   * - キャンセルボタン押下も考慮し、宛先施設関係者の情報はコピーしたものを渡す
   */
  public onOtherUserGroupsSelect(otherUserTargets: Array<OtherUserGroupTypeGroup>): void {
    const copy: Array<OtherUserGroupTypeGroup> = JSON.parse(JSON.stringify(otherUserTargets));
    this.otherUserGroupSelectModal.show(copy);
    return;
  }

  /**
   * ### 施設関係者個別の宛先選択モーダルも完了イベント
   */
  public addOtherUserGroupTarget(targets: Array<OtherUserGroupTypeGroup>): void {
    this.sendMessageTargetsInput._otherUserTargets = targets;
    return;
  }

  /**
   * ### 宛先グループの読み込み完了イベント
   *
   * - 子コンポーネントから呼び出される
   * - 一時保存されたアンケート情報の送信種別が宛先グループであれば子コンポーネントに宛先を渡して復元させる
   */
  public loadedGroupTargets(): void {
    if (
      this.questionnaire &&
      (this.questionnaire.targetType == TargetType.SEND_MESSAGE_GROUPS ||
        this.questionnaire.targetType == TargetType.RESIDENT_GROUPS ||
        this.questionnaire.targetType == TargetType.OTHER_USER_GROUPS)
    ) {
      const questionnaire = this.questionnaire;
      this.sendMessageTargetsInput.restoreGroupTargets(questionnaire.sendMessageGroupInfos, questionnaire.residentGroupInfos, questionnaire.otherUserGroupInfos);
    }
    // TODO ここで読込み完了ではなく、createView()でやりたい
    this.isBusy = false;
    return;
  }

  /**
   * ### 宛先グループの宛先ボタン押下イベント
   *
   * - モーダルで宛先グループを選択させる
   * - キャンセルボタン押下も考慮し、宛先グループの情報はコピーしたものを渡す
   */
  public onSendMessageGroupSelect(groupTargets: GroupModel): void {
    const copy: GroupModel = JSON.parse(JSON.stringify(groupTargets));
    this.sendMessageGroupSelectModal.show(copy);
    return;
  }

  /**
   * ### 宛先グループの宛先選択モーダルも完了イベント
   */
  public addSendGroupTarget(targets: GroupModel): void {
    this.sendMessageTargetsInput._groupTargets = targets;
    return;
  }

  /**
   * ### 一時保存確認モーダルを表示する
   */
  public async showUpdateConfirmModal(questionnaire: QuestionnairesGetModel): Promise<void> {
    if (!questionnaire.sendDatetime) {
      this.updateModalTitle = "アンケートを一時保存します。よろしいですか？";
    } else {
      // 受付中を受付終了に変更した場合
      if (questionnaire.acceptingAnswers == true && this.acceptingAnswers == false) {
        this.updateModalTitle = "アンケートを更新します。\n回答期限は自動的に現在日時になります。\nよろしいですか？";
      }
      // 受付終了を受付中に変更した場合
      else if (questionnaire.acceptingAnswers == false && this.acceptingAnswers == true) {
        let replyLimit: string = "無期限";
        if (this.enableAnswerLimit == true) {
          if (this.replyLimit == null) {
            replyLimit = "無期限";
          } else {
            replyLimit = this.$dateUtil.convertNumberToString(this.replyLimit, "yyyy年mm月dd日 hh:MM");
          }
        }
        this.updateModalTitle = `アンケートを更新します。\n回答期限が${replyLimit}になっています。\nよろしいですか？`;
      } else {
        this.updateModalTitle = "アンケートを更新します。よろしいですか？";
      }
    }
    this.showUpdateModal = true;
    return;
  }

  /**
   * ### アンケートを一時保存または更新（送信後の更新）する
   */
  public async updateQuestionnaire(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      // ファイルが添付されていればファイルのアップロード
      let fileUrls: Array<FileUrl> = [];
      // ファイルアップロードするタイミングでファイルが削除（移動含む）、リネームされた場合は失敗するのでここでtry-catchする
      // アップロードに失敗した場合は添付されたファイルを全てリセットしてモーダルで再添付を促す
      try {
        if (0 < this.files.length) {
          const targetFiles = [];
          for (const file of this.files) {
            if (file.type == "existing" && !file.fileObject) {
              targetFiles.push(await this.getS3File(file.url as string, file.name));
            } else {
              targetFiles.push(file.fileObject as File);
            }
          }
          const urls: Array<S3LocationResponse> = await this.createUploadUrls(targetFiles, S3LocationType.QUESTIONNAIRE);
          fileUrls = await this.uploadFiles(urls, targetFiles);
        }
      } catch (error) {
        this.isBusy = false;

        this.showUploadErrorModal = true;
        this.files = [];

        return;
      }

      this.setScreenInputTemporaryStore();
      const questionnaire: QuestionnairesGetModel = this.questionnaire as QuestionnairesGetModel;
      let targets: { targets: string[]; lineTargets: string[]; mailTargets: string[] } = { targets: [], lineTargets: [], mailTargets: [] };
      if (questionnaire.sendDatetime) {
        targets.targets = questionnaire.targets;
        targets.lineTargets = questionnaire.lineTargets;
        targets.mailTargets = questionnaire.mailTargets;
      } else {
        targets = await this.sendMessageTargetsInput.createTargets();
      }
      const requestBody: QuestionnairesPatchModel = this.createQuestionnairesPatchModel(targets, fileUrls);
      const id: number = Number(this.$route.params.id);
      await this.questionnairesService.patch(userStore, id, requestBody);
      this.resetScreenInputTemporaryStore();

      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### ファイルアップロード先のURLを発行する
   */
  public async createUploadUrls(files: Array<File>, s3LocationType: S3LocationType): Promise<Array<S3LocationResponse>> {
    let extensions: Array<string> = [];
    for (const file of files) {
      const _file: Array<string> = file.name.split("/");
      const _extensions: Array<string> = _file[_file.length - 1].split(".");
      extensions.push("." + _extensions[_extensions.length - 1]);
    }
    const params: S3LocationMessage = {
      userId: userStore.loginUser.userId
    };
    const requestBody: S3LocationRequest = {
      type: s3LocationType,
      count: files.length,
      extensions: extensions,
      params: params,
      acl: "public-read"
    };
    const url: Array<S3LocationResponse> = await this.s3LocationsService.post(userStore, requestBody);

    return url;
  }

  /**
   * ### プログレスローディングを表示してファイルアップロードする
   */
  public async uploadFiles(urls: Array<S3LocationResponse>, files: Array<File>): Promise<Array<FileUrl>> {
    const fileUrls: Array<FileUrl> = [];

    try {
      // プログレス付きモーダルを表示する
      // 進捗はファイルのアプロード状況を反映させるようにする
      this.currentUploadCount = 0;
      this.uploadCount = this.files.length;
      this.progressMessage = "ファイルアップロード中。\n完了するまでそのままお待ちください。\nメッセージ送信中に別画面に遷移しますと処理がキャンセルされます。";
      this.progressLoading = true;
      // アップロード
      for (let i = 0; i < urls.length; i++) {
        this.currentUploadCount = i;
        await this.s3UploadsService.uploadFile(urls[i], files[i]);

        // DBにはファイルではなく、添付ファイルのS3URLを保持するためURL配列を作成する
        const preUrl: string = urls[i].url.endsWith("/") ? urls[i].url : `${urls[i].url}/`;
        const s3Url: string = `${preUrl}${urls[i].fields["key"]}`;

        const index: number = this.allowContentType.indexOf(files[i].type);
        const type: string = this.allowMessageType[index];

        fileUrls.push({
          name: this.files[i].name,
          type: type,
          url: s3Url
        });
      }
      // アップロードしたファイルはサムネイル画像が作成されたり、movからmp4への変換が行われる
      // ファイルの存在確認をして変換されたか確認する
      await this.existFile(fileUrls);
    } finally {
      this.progressLoading = false;
    }

    return fileUrls;
  }

  /**
   * ### ファイルの存在確認を行う
   */
  public async existFile(fileUrls: Array<FileUrl>): Promise<void> {
    // ファイルがアップロードされているかをチェックする
    const s3Urls: string[] = [];
    for (const fileUrl of fileUrls) {
      s3Urls.push(fileUrl.url);
    }
    const fileRequestBody: ExistS3FilesPostModel = { urls: s3Urls };
    await this.existS3FilesService.post(userStore, fileRequestBody);

    // サムネイルのファイルが存在しているかをチェックする
    let thumbnailUrls: Array<string> = [];
    for (const s3Url of s3Urls) {
      // サムネイルのファイル名は<元ファイル名>_thumbnailになる
      const splitedUrl: string[] = s3Url.split("/");
      const splitedFileName: string[] = splitedUrl[splitedUrl.length - 1].split(".");

      // mov、mp4以外の場合はサムネイルをメッセージ送信時に作成するのでスキップ
      if (splitedFileName[1] != "mov" && splitedFileName[1] != "mp4") {
        continue;
      }

      const thumbnailFileName: string = splitedFileName[0] + "_thumbnail.jpeg";
      let thunmbnailUrl: string = "";
      for (let i = 0; i < splitedUrl.length - 1; i++) {
        thunmbnailUrl += splitedUrl[i] + "/";
      }
      thunmbnailUrl += thumbnailFileName;
      thumbnailUrls.push(thunmbnailUrl);
    }

    if (0 < thumbnailUrls.length) {
      const thumbnailRequestBody: ExistS3FilesPostModel = { urls: thumbnailUrls };
      await this.existS3FilesService.post(userStore, thumbnailRequestBody);
    }

    return;
  }

  public createQuestionnairesPatchModel(targets: { targets: string[]; lineTargets: string[]; mailTargets: string[] }, fileUrls: Array<FileUrl>): QuestionnairesPatchModel {
    const questionnaire: QuestionnairesGetModel = this.questionnaire as QuestionnairesGetModel;

    let replyLimit: number | null = this.enableAnswerLimit == true ? this.replyLimit : null;
    if (questionnaire.sendDatetime) {
      // 受付中を受付終了に変更した場合は回答期限を格納する(上書きもok)
      if (questionnaire.acceptingAnswers == true && this.acceptingAnswers == false) {
        replyLimit = this.$dateUtil.convertDateToNumber(new Date(), "yyyymmddhhMMss000");
      }
    }

    return {
      title: this.title ? this.title : "",
      text: this.text ? this.text : "",
      fileUrls: fileUrls,
      hidden: this.hidden,
      messageTitle: this.messageTitle ? this.messageTitle : "",
      messageText: this.messageText ? this.messageText : "",
      targets: targets.targets,
      lineTargets: targets.lineTargets,
      mailTargets: targets.mailTargets,
      sendMessageGroupInfos: questionnaire.sendDatetime ? questionnaire.sendMessageGroupInfos : this.sendMessageTargetsInput.createSendMessageGroupInfos(),
      residentGroupInfos: questionnaire.sendDatetime ? questionnaire.residentGroupInfos : this.sendMessageTargetsInput.createResidentGroupInfos(),
      otherUserGroupInfos: questionnaire.sendDatetime ? questionnaire.otherUserGroupInfos : this.sendMessageTargetsInput.createOtherUserGroupInfos(),
      targetType: this.createTargetType(this.targetType as SendMessageTarget, this.groupType) as any,
      questions: this.createQuestionTemplateItems(this.questions),
      replyLimit: replyLimit,
      needSendMessage: false, // メッセージ送信はしない
      acceptingAnswers: this.acceptingAnswers as boolean,
      shareStandardUsers: this.createRequestShareStandardUsers(this.shareStandardUsers),
      updated: questionnaire.updated
    };
  }

  public createTargetType(targetType: SendMessageTarget, groupType: GroupType): TargetType | null {
    switch (targetType) {
      case SendMessageTarget.ALL_FAMILIES: {
        return TargetType.ALL_FAMILIES;
      }
      case SendMessageTarget.FAMILIES: {
        return TargetType.FAMILIES;
      }
      case SendMessageTarget.OTHER_USERS: {
        return TargetType.OTHER_USERS;
      }
      case SendMessageTarget.GROUPS: {
        switch (groupType) {
          case GroupType.FAMILY:
          case GroupType.OTHER_USER: {
            return TargetType.SEND_MESSAGE_GROUPS;
          }
          case GroupType.RESIDENT_GROUP: {
            return TargetType.RESIDENT_GROUPS;
          }
          case GroupType.OTHER_USER_GROUP: {
            return TargetType.OTHER_USER_GROUPS;
          }
        }
      }
    }
    return null;
  }

  public createQuestionTemplateItems(questions: Array<QuestionInputItemModel>): Array<QuestionTemplateItem> {
    let questionTemplateItems: Array<QuestionTemplateItem> = [];
    for (const question of questions) {
      // selectはオプションを足したり消したりできるので、value値はここで連番に整える必要がある
      if (question.type == TemplateType.SELECT) {
        const selectQuestion: QuestionInputSelectItemModel = question as QuestionInputSelectItemModel;
        for (let i = 0; i < selectQuestion.options.length; i++) {
          selectQuestion.options[i].value = `${i}`;
        }
      }
      // isFocusedでプロパティ削除
      const questionTemplateItem: any = question;
      delete questionTemplateItem.isFocused;

      questionTemplateItems.push(questionTemplateItem);
    }
    return questionTemplateItems;
  }

  public createRequestShareStandardUsers(shareStandardUserCheckboxs: Array<ShareStandardUserCheckbox>): string[] {
    const shareStandardUsers: string[] = [];
    for (const checkbox of shareStandardUserCheckboxs) {
      if (checkbox.answer == true) {
        shareStandardUsers.push(checkbox.id);
      }
    }
    return shareStandardUsers;
  }

  /**
   * ### 送信確認モーダルを表示する
   */
  public async showSendConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    const questionsValidationResult: boolean = this.validateQuestions();
    const targetsValidationResult: boolean = await this.validateTargets();
    const fileValidationResult: boolean = this.validateFiles();

    if (validationResult && questionsValidationResult && targetsValidationResult && fileValidationResult) {
      this.showValidationAlert = false;
      this.showSendModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  public validateQuestions(): boolean {
    this.questionErrorMessage = "";
    if (this.questions.length < 1) {
      this.questionErrorMessage = "質問項目は必須入力です";
      return false;
    } else {
      return true;
    }
  }

  public async validateTargets(): Promise<boolean> {
    this.targetErrorMessage = "";
    const targets: { targets: string[]; lineTargets: string[]; mailTargets: string[] } = await this.sendMessageTargetsInput.createTargets();
    if (targets.targets.length < 1) {
      this.targetErrorMessage = "宛先にユーザーがいないため、送信できません";
      return false;
    } else {
      return true;
    }
  }

  /**
   * ### ファイルバリデーション
   *
   * - 何らかの原因でファイルが存在しないパターンがあったので、チェック処理を行う
   */
  public validateFiles(): boolean {
    for (const file of this.files) {
      if (!file || !file.name || (file.fileObject && file.fileObject.size == 0)) {
        this.$validator.errors.add({
          field: "tempfiles-multiple-register-errors",
          msg: `ファイルが破損等をしている可能性があり、ファイルを認識することができません。お手数ですが、再度選択し直してください。`
        });
        return false;
      }
    }
    return true;
  }

  /**
   * ### アンケートを送信する
   */
  public async sendQuestionnaire(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "送信中...";

    try {
      // ファイルが添付されていればファイルのアップロード
      let fileUrls: Array<FileUrl> = [];
      // ファイルアップロードするタイミングでファイルが削除（移動含む）、リネームされた場合は失敗するのでここでtry-catchする
      // アップロードに失敗した場合は添付されたファイルを全てリセットしてモーダルで再添付を促す
      try {
        if (0 < this.files.length) {
          const targetFiles = [];
          for (const file of this.files) {
            if (file.type == "existing" && !file.fileObject) {
              targetFiles.push(await this.getS3File(file.url as string, file.name));
            } else {
              targetFiles.push(file.fileObject as File);
            }
          }
          const urls: Array<S3LocationResponse> = await this.createUploadUrls(targetFiles, S3LocationType.QUESTIONNAIRE);
          fileUrls = await this.uploadFiles(urls, targetFiles);
        }
      } catch (error) {
        this.isBusy = false;

        this.showUploadErrorModal = true;
        this.files = [];

        return;
      }

      this.setScreenInputTemporaryStore();
      const targets: { targets: string[]; lineTargets: string[]; mailTargets: string[] } = await this.sendMessageTargetsInput.createTargets();
      const requestBody: QuestionnairesPatchModel = this.createQuestionnairesPatchModel(targets, fileUrls);
      requestBody.needSendMessage = true; // メッセージ送信する
      const id: number = Number(this.$route.params.id);
      await this.questionnairesService.patch(userStore, id, requestBody);
      this.resetScreenInputTemporaryStore();

      // TODO API側でアンケートを登録⇒LINE送信⇒アンケートを更新するので、処理に時間がかかるため、タイマーセットしてDB更新を待つ
      // APIから処理完了を受信する方法がありそうだが、現状は不明なのでタイマーで対応している
      await this.$timerUtil.setTimeout(5000);

      this.showSendSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }
    return;
  }

  /**
   * ### 画面入力情報をstoreから復元し、store情報を初期化する
   */
  public restoreScreenInputTemporaryStore(): void {
    if (screenInputTemporaryStore.url == PcUrl.QuestionnaireEdit) {
      this.title = screenInputTemporaryStore.inputParams.title;
      this.text = screenInputTemporaryStore.inputParams.text;
      this.hidden = screenInputTemporaryStore.inputParams.hidden;
      const questions: Array<QuestionInputItemModel> = [];
      for (const questionItem of screenInputTemporaryStore.inputParams.questions) {
        questions.push({
          ...questionItem,
          isFocused: false
        });
      }
      this.questions = questions;
      this.enableAnswerLimit = screenInputTemporaryStore.inputParams.enableAnswerLimit;
      this.replyLimit = screenInputTemporaryStore.inputParams.replyLimit;
      this.acceptingAnswers = screenInputTemporaryStore.inputParams.acceptingAnswers;
      this.targetType = screenInputTemporaryStore.inputParams.targetType;
      this.messageTitle = screenInputTemporaryStore.inputParams.messageTitle;
      this.messageText = screenInputTemporaryStore.inputParams.messageText;
      this.shareStandardUsers = screenInputTemporaryStore.inputParams.shareStandardUsers;

      this.resetScreenInputTemporaryStore();
    }
    return;
  }

  /**
   * ### 画面入力情報のstoreをリセットする
   */
  public resetScreenInputTemporaryStore(): void {
    screenInputTemporaryStore.initializeScreenInputTemporary();
    return;
  }

  /**
   * ### 画面入力情報をstoreに格納する
   *
   * - 一時間無操作タイムアウト対策としてAPIリクエスト前にstoreに保存する
   */
  public setScreenInputTemporaryStore(): void {
    screenInputTemporaryStore.setScreenInputTemporary({
      url: PcUrl.QuestionnaireEdit,
      inputParams: {
        title: this.title,
        text: this.text,
        hidden: this.hidden,
        questions: this.questions,
        enableAnswerLimit: this.enableAnswerLimit,
        replyLimit: this.replyLimit,
        targetType: this.targetType,
        messageTitle: this.messageTitle,
        messageText: this.messageText,
        shareStandardUsers: this.shareStandardUsers
      }
    });
    return;
  }

  /**
   * ### アンケート一覧画面へ遷移する
   */
  public gotoQuestionnaireList(): void {
    this.$router.push(PcUrl.QuestionnaireList);
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

// 親要素からネストしてスタイルを指定する
#questionnaires-edit {
  max-width: 660px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    width: 100%;

    .title {
      font-size: 22px;
      font-weight: bold;
    }
  }

  .sub-title {
    font-size: 18px;
    font-weight: bold;
  }

  .toast-title {
    font-size: 16px;
    color: $font-base-color;
  }

  .add-question-buttons {
    padding: 0 20px 0 20px;

    .input-select {
      display: block;
      min-width: 320px;
      max-width: 320px;
      height: 42px;
      padding: 6px 12px;
      background-color: $bg-default;
      border: 1px solid $form-border-color;
      border-radius: 0.25rem;
      &:focus {
        // chromeが自動的に付与するスタイルを無効化する
        outline: none;
        box-shadow: 0 0 0 0.2rem $form-boxshadow-select-color;
        border: 1px solid $form-border-select-color !important;
        border-radius: 0.25rem;
      }
    }
    .add-question-btn {
      width: 140px;
    }
  }

  .questionnaires,
  .accepted-message,
  .share-standard-users {
    padding: 0 20px 0 20px;
  }

  .update-btn,
  .send-btn,
  .cancel-btn {
    width: 120px;
  }

  // 子コンポーネントに強制的にスタイルを指定しないtoastの幅が小さい
  ::v-deep {
    .b-toast,
    .toast {
      width: 400px !important;
      max-width: 400px !important;
    }
    .questionnaire-item-btn {
      height: 80px;
      width: 180px;
      border: 1px solid $border-darkgray;

      &:hover {
        background-color: $btn-hover !important;
        transition: background-color 0.25s ease-out, box-shadow 0.25s ease-out, color 0.25s ease-out;
      }

      .questionnaire-item {
        font-size: 20px;
        color: $font-base-color;
        opacity: 0.75;
        text-shadow: 0 1px 0 #fff;
      }
    }
  }
}
</style>
